import React from 'react';
import styled from 'styled-components';

// Lightweight, dependency-free Markdown renderer for the AI chat.
// It builds React elements directly (no dangerouslySetInnerHTML), so it is XSS-safe by
// construction: only known element types and text nodes are produced, and links are
// restricted to safe schemes. Supports the subset the assistant is prompted to emit:
// headings, bold/italic, inline code, links, ordered/unordered lists, GFM pipe tables,
// fenced code blocks, blockquotes, horizontal rules — plus a ```chart bar-chart block.

const PURPLE = '#48237D';

const Root = styled.div`
    font-size: 14px;
    line-height: 1.45;
    color: #1f2533;

    & > *:first-child {
        margin-top: 0;
    }
    & > *:last-child {
        margin-bottom: 0;
    }

    p {
        margin: 0 0 8px;
    }
    h1,
    h2,
    h3,
    h4 {
        margin: 12px 0 6px;
        line-height: 1.25;
        font-weight: 600;
        color: ${PURPLE};
    }
    h1 {
        font-size: 17px;
    }
    h2 {
        font-size: 16px;
    }
    h3 {
        font-size: 15px;
    }
    h4 {
        font-size: 14px;
    }

    ul,
    ol {
        margin: 0 0 8px;
        padding-left: 20px;
    }
    li {
        margin: 2px 0;
    }

    a {
        color: ${PURPLE};
        text-decoration: underline;
        word-break: break-word;
    }

    code {
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 12.5px;
        background: #f0edf6;
        color: #4a2d7a;
        padding: 1px 5px;
        border-radius: 4px;
    }
    pre {
        margin: 0 0 8px;
        padding: 10px 12px;
        background: #2b2540;
        color: #f6f4fb;
        border-radius: 8px;
        overflow-x: auto;
    }
    pre code {
        background: transparent;
        color: inherit;
        padding: 0;
        font-size: 12.5px;
    }

    blockquote {
        margin: 0 0 8px;
        padding: 4px 12px;
        border-left: 3px solid ${PURPLE};
        background: #f7f5fb;
        color: #444;
        border-radius: 0 6px 6px 0;
    }

    hr {
        border: none;
        border-top: 1px solid #e3e0ec;
        margin: 10px 0;
    }

    .md-table-wrap {
        overflow-x: auto;
        margin: 0 0 8px;
    }
    table {
        border-collapse: collapse;
        width: 100%;
        font-size: 13px;
    }
    th,
    td {
        border: 1px solid #e3e0ec;
        padding: 5px 8px;
        text-align: left;
        vertical-align: top;
    }
    thead th {
        background: ${PURPLE};
        color: #fff;
        font-weight: 600;
        white-space: nowrap;
    }
    tbody tr:nth-child(even) {
        background: #faf9fc;
    }
`;

const ChartTitle = styled.div`
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 6px;
    color: ${PURPLE};
`;

const ChartRow = styled.div`
    display: grid;
    grid-template-columns: 90px 1fr 44px;
    align-items: center;
    gap: 8px;
    margin: 3px 0;
    font-size: 12.5px;
`;

const ChartLabel = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #444;
`;

const ChartTrack = styled.div`
    background: #efecf6;
    border-radius: 4px;
    overflow: hidden;
    height: 14px;
`;

const ChartValue = styled.div`
    text-align: right;
    color: #555;
    font-variant-numeric: tabular-nums;
`;

const ChartCard = styled.div`
    margin: 0 0 8px;
    padding: 10px 12px;
    border: 1px solid #e3e0ec;
    border-radius: 8px;
    background: #fff;
`;

const OptionsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 2px 0 10px;
`;

const OptionBox = styled.button`
    border: 1px solid ${PURPLE};
    background: #f7f5fb;
    color: ${PURPLE};
    border-radius: 8px;
    padding: 6px 12px;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.3;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        background: ${PURPLE};
        color: #fff;
    }
    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;

const safeUrl = (raw: string): string | null => {
    const url = raw.trim();
    if (/^(https?:|mailto:)/i.test(url)) return url;
    if (url.startsWith('/')) return url;
    return null;
};

// ----- inline (bold / italic / code / links) -----

const renderInline = (text: string, keyBase: string): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    let rest = text;
    let i = 0;
    let buf = '';

    const flush = () => {
        if (buf) {
            out.push(buf);
            buf = '';
        }
    };

    const patterns: { re: RegExp; node: (m: RegExpMatchArray, key: string) => React.ReactNode }[] = [
        { re: /^`([^`]+)`/, node: (m, k) => <code key={k}>{m[1]}</code> },
        { re: /^\*\*([^*]+)\*\*/, node: (m, k) => <strong key={k}>{renderInline(m[1], k)}</strong> },
        { re: /^__([^_]+)__/, node: (m, k) => <strong key={k}>{renderInline(m[1], k)}</strong> },
        { re: /^\*([^*]+)\*/, node: (m, k) => <em key={k}>{renderInline(m[1], k)}</em> },
        { re: /^_([^_]+)_/, node: (m, k) => <em key={k}>{renderInline(m[1], k)}</em> },
        {
            re: /^\[([^\]]+)\]\(([^)]+)\)/,
            node: (m, k) => {
                const href = safeUrl(m[2]);
                if (!href) return <span key={k}>{m[0]}</span>;
                // Relative ("/services") links are in-app routes — render them as plain anchors and let
                // the Root click handler navigate via the SPA router (no full reload / new tab). External
                // (http/mailto) links open in a new tab as before.
                const internal = href.startsWith('/');
                return internal ? (
                    <a key={k} href={href}>
                        {m[1]}
                    </a>
                ) : (
                    <a key={k} href={href} target="_blank" rel="noopener noreferrer">
                        {m[1]}
                    </a>
                );
            },
        },
    ];

    while (rest.length) {
        let matched = false;
        for (const p of patterns) {
            const m = rest.match(p.re);
            if (m) {
                flush();
                out.push(p.node(m, `${keyBase}-${i++}`));
                rest = rest.slice(m[0].length);
                matched = true;
                break;
            }
        }
        if (!matched) {
            buf += rest[0];
            rest = rest.slice(1);
        }
    }
    flush();
    return out;
};

// ----- table / chart helpers -----

const splitRow = (line: string): string[] => {
    let s = line.trim();
    if (s.startsWith('|')) s = s.slice(1);
    if (s.endsWith('|')) s = s.slice(0, -1);
    return s.split('|').map((c) => c.trim());
};

// A GFM table separator row, e.g. `| --- | :--: | ---: |`. Validated with a single linear scan
// (no regex) so it runs in O(n) and cannot exhibit super-linear backtracking on adversarial input.
const isTableSeparator = (line: string): boolean => {
    let body = line.trim();
    if (body === '') return false;
    if (body.startsWith('|')) body = body.slice(1);
    if (body.endsWith('|')) body = body.slice(0, -1);

    let sawCell = false;
    for (const rawCell of body.split('|')) {
        const cell = rawCell.trim();
        let i = 0;
        if (cell[i] === ':') i++; // optional left alignment colon
        let dashes = 0;
        while (cell[i] === '-') {
            i++;
            dashes++;
        }
        if (cell[i] === ':') i++; // optional right alignment colon
        // The cell must be exactly :?-{2,}:? with nothing left over.
        if (i !== cell.length || dashes < 2) return false;
        sawCell = true;
    }
    return sawCell;
};

interface ChartPoint {
    label: string;
    value: number;
}

const BarChart: React.FC<{ title?: string; data: ChartPoint[] }> = ({ title, data }) => {
    const max = Math.max(...data.map((d) => d.value), 0) || 1;
    return (
        <ChartCard>
            {title && <ChartTitle>{title}</ChartTitle>}
            {data.map((d, i) => (
                <ChartRow key={i}>
                    <ChartLabel title={d.label}>{d.label}</ChartLabel>
                    <ChartTrack>
                        <div
                            style={{
                                width: `${Math.max(2, (d.value / max) * 100)}%`,
                                height: '100%',
                                background: PURPLE,
                            }}
                        />
                    </ChartTrack>
                    <ChartValue>{d.value}</ChartValue>
                </ChartRow>
            ))}
        </ChartCard>
    );
};

const parseChart = (raw: string): { title?: string; data: ChartPoint[] } | null => {
    try {
        const obj = JSON.parse(raw);
        const data = Array.isArray(obj?.data) ? obj.data : null;
        if (!data) return null;
        const points: ChartPoint[] = data
            .map((d: { label?: unknown; value?: unknown }) => ({
                label: String(d.label ?? ''),
                value: Number(d.value),
            }))
            .filter((d: ChartPoint) => Number.isFinite(d.value));
        if (!points.length) return null;
        return { title: typeof obj.title === 'string' ? obj.title : undefined, data: points };
    } catch {
        return null;
    }
};

// ----- clickable options / commands -----

interface ChatOption {
    label: string;
    value: string;
}

// A ```options block: JSON of clickable choices/commands the user can tap to send as their next
// message. Accepts {"options":[...]} or a bare array; each entry is a string (label = value) or
// {label, value}. `label` is the button text; `value` is what gets sent on click.
const parseOptions = (raw: string): ChatOption[] | null => {
    try {
        const obj = JSON.parse(raw);
        const arr = Array.isArray(obj) ? obj : Array.isArray(obj?.options) ? obj.options : null;
        if (!arr) return null;
        const opts: ChatOption[] = arr
            .map((o: unknown): ChatOption => {
                if (typeof o === 'string') return { label: o.trim(), value: o.trim() };
                if (o && typeof o === 'object') {
                    const rec = o as { label?: unknown; value?: unknown };
                    const label = String(rec.label ?? rec.value ?? '').trim();
                    const value = String(rec.value ?? rec.label ?? '').trim();
                    return { label, value };
                }
                return { label: '', value: '' };
            })
            .filter((o: ChatOption) => o.label !== '' && o.value !== '');
        return opts.length ? opts : null;
    } catch {
        return null;
    }
};

const OptionsBlock: React.FC<{ options: ChatOption[]; onAction?: (value: string) => void }> = ({
    options,
    onAction,
}) => (
    <OptionsWrap>
        {options.map((o, i) => (
            <OptionBox
                key={i}
                type="button"
                disabled={!onAction}
                title={o.value}
                onClick={() => onAction?.(o.value)}
            >
                {o.label}
            </OptionBox>
        ))}
    </OptionsWrap>
);

// ----- block-level parsing -----

const Markdown: React.FC<{
    content: string;
    onAction?: (value: string) => void;
    onNavigate?: (href: string) => void;
}> = ({ content, onAction, onNavigate }) => {
    const lines = content.replace(/\r\n/g, '\n').split('\n');
    const blocks: React.ReactNode[] = [];
    let key = 0;
    let i = 0;

    const para: string[] = [];
    const flushParagraph = () => {
        if (para.length) {
            const text = para.join(' ');
            blocks.push(<p key={`b-${key++}`}>{renderInline(text, `p-${key}`)}</p>);
            para.length = 0;
        }
    };

    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // blank line -> paragraph break
        if (trimmed === '') {
            flushParagraph();
            i++;
            continue;
        }

        // fenced code / chart block
        const fence = trimmed.match(/^```(\w*)\s*$/);
        if (fence) {
            flushParagraph();
            const lang = fence[1];
            const body: string[] = [];
            i++;
            while (i < lines.length && !/^```\s*$/.test(lines[i].trim())) {
                body.push(lines[i]);
                i++;
            }
            i++; // closing fence
            const raw = body.join('\n');
            const chart = lang === 'chart' ? parseChart(raw) : null;
            const options = lang === 'options' || lang === 'actions' ? parseOptions(raw) : null;
            if (chart) {
                blocks.push(<BarChart key={`b-${key++}`} title={chart.title} data={chart.data} />);
            } else if (options) {
                blocks.push(<OptionsBlock key={`b-${key++}`} options={options} onAction={onAction} />);
            } else {
                blocks.push(
                    <pre key={`b-${key++}`}>
                        <code>{raw}</code>
                    </pre>
                );
            }
            continue;
        }

        // heading: 1-4 leading '#' followed by whitespace, then the heading text.
        // Parsed with a linear scan (no backtracking regex) to avoid any super-linear runtime.
        let hashes = 0;
        while (hashes < trimmed.length && trimmed[hashes] === '#') hashes++;
        // `/\s/` here only tests a single character (no quantifier), so it is constant-time.
        if (hashes >= 1 && hashes <= 4 && /\s/.test(trimmed.charAt(hashes))) {
            flushParagraph();
            const Tag = (`h${hashes}` as unknown) as keyof JSX.IntrinsicElements;
            const text = trimmed.slice(hashes).trimStart();
            blocks.push(<Tag key={`b-${key++}`}>{renderInline(text, `h-${key}`)}</Tag>);
            i++;
            continue;
        }

        // horizontal rule (not a table separator)
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
            flushParagraph();
            blocks.push(<hr key={`b-${key++}`} />);
            i++;
            continue;
        }

        // table: header row followed by a separator row
        if (trimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
            flushParagraph();
            const header = splitRow(line);
            i += 2; // skip header + separator
            const rows: string[][] = [];
            while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
                rows.push(splitRow(lines[i]));
                i++;
            }
            blocks.push(
                <div className="md-table-wrap" key={`b-${key++}`}>
                    <table>
                        <thead>
                            <tr>
                                {header.map((h, hi) => (
                                    <th key={hi}>{renderInline(h, `th-${key}-${hi}`)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, ri) => (
                                <tr key={ri}>
                                    {header.map((_, ci) => (
                                        <td key={ci}>{renderInline(r[ci] ?? '', `td-${key}-${ri}-${ci}`)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
            continue;
        }

        // blockquote
        if (/^>\s?/.test(trimmed)) {
            flushParagraph();
            const quote: string[] = [];
            while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
                quote.push(lines[i].trim().replace(/^>\s?/, ''));
                i++;
            }
            blocks.push(
                <blockquote key={`b-${key++}`}>{renderInline(quote.join(' '), `bq-${key}`)}</blockquote>
            );
            continue;
        }

        // unordered list
        if (/^[-*+]\s+/.test(trimmed)) {
            flushParagraph();
            const items: string[] = [];
            while (i < lines.length && /^[-*+]\s+/.test(lines[i].trim())) {
                items.push(lines[i].trim().replace(/^[-*+]\s+/, ''));
                i++;
            }
            blocks.push(
                <ul key={`b-${key++}`}>
                    {items.map((it, ii) => (
                        <li key={ii}>{renderInline(it, `li-${key}-${ii}`)}</li>
                    ))}
                </ul>
            );
            continue;
        }

        // ordered list
        if (/^\d+\.\s+/.test(trimmed)) {
            flushParagraph();
            const items: string[] = [];
            while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
                items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
                i++;
            }
            blocks.push(
                <ol key={`b-${key++}`}>
                    {items.map((it, ii) => (
                        <li key={ii}>{renderInline(it, `oli-${key}-${ii}`)}</li>
                    ))}
                </ol>
            );
            continue;
        }

        // default: paragraph text
        para.push(trimmed);
        i++;
    }
    flushParagraph();

    // Intercept clicks on in-app (relative) links and route them through the SPA navigator instead of
    // a full page load. Honour modifier-clicks / non-primary buttons so "open in new tab" still works.
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!onNavigate) return;
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const anchor = (e.target as HTMLElement).closest('a');
        const href = anchor?.getAttribute('href');
        if (anchor && href && href.startsWith('/')) {
            e.preventDefault();
            onNavigate(href);
        }
    };

    return <Root onClick={handleClick}>{blocks}</Root>;
};

export default Markdown;
